import { type RefObject, useLayoutEffect, useState } from 'react';

/**
 * Hook to check if an element is overflowing. This is useful for displaying a "show more" button when the element is overflowing.
 *
 * @param ref - The ref to the element to check for overflow, initialized with const ref = useRef(null), then passing to the div as ref={ref}.
 * @param callback - An optional callback to call when the overflow state changes to true.
 * @returns The overflow state of the element.
 */
export const useIsOverflow = (ref: RefObject<HTMLElement>, callback?: (hasOverflow: boolean) => void) => {
  const [isOverflow, setIsOverflow] = useState<boolean | undefined>(undefined);

  useLayoutEffect(() => {
    const { current } = ref;

    const trigger = () => {
      const hasOverflow = current && current.scrollHeight > current.clientHeight;

      setIsOverflow(hasOverflow ?? false);

      if (callback) {
        callback(hasOverflow ?? false);
      }
    };

    if (current) {
      trigger();
    }
  }, [callback, ref]);

  return !!isOverflow;
};
